<template>
  <div
    v-show="show"
    ref="el"
    class="container"
    :style="{
      '--spacing': `${spacing}rem`,
      marginTop: `calc(-${size}px - (var(--spacing) * 2))`,
      textAlign: align,
    }"
  >
    <v-btn
      class="button border elevation-10"
      :style="{
        fontSize: `${size / 36}rem`,
      }"
      color="secondary"
      variant="flat"
      :icon="icon"
      :size="size"
      @click="targetEl?.scrollIntoView({ behavior })"
    />
  </div>
</template>

<script setup lang="ts">
import { findScrollContainer } from '@/lib/dom'
import { computed, onBeforeUnmount, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    target?: any
    icon?: string
    behavior?: ScrollBehavior
    size?: number
    spacing?: number
    align?: 'left' | 'right'
  }>(),
  {
    target: undefined,
    icon: '$up',
    behavior: 'smooth',
    size: 48,
    spacing: 1.5,
    align: 'right',
  }
)

const show = ref(false)
const el = ref()
const targetEl = computed<HTMLElement | undefined>(
  // component ref > element ref > parent element
  () => props.target?.$el ?? props.target ?? el.value?.parentElement
)

const scrollContainer = ref()
function scroll() {
  if (!targetEl.value) {
    return
  }
  if (targetEl.value.getBoundingClientRect().top < 0) {
    show.value = true
    return
  }

  show.value = false
}
watch(
  () => targetEl.value,
  (el) => {
    scrollContainer.value?.removeEventListener('scroll', scroll)
    scrollContainer.value = findScrollContainer(el)
    scrollContainer.value?.addEventListener('scroll', scroll)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  scrollContainer.value?.removeEventListener('scroll', scroll)
})
</script>

<style scoped>
.container {
  position: sticky;
  bottom: 0;
  pointer-events: none;
}

.button {
  margin: var(--spacing);
  pointer-events: auto;
}
</style>
